import { EventType } from "@/analytics/analytics-events";
import { ToggleRegistrationEditModeProperties } from "@/analytics/staging-area-workflows-events-properties";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectHasWritePermission } from "@/store/user-selectors";
import {
  blue,
  ChangeIndicatorBadge,
  FaroSvgIcon,
  FaroTooltip,
  OpenToastOptions,
  Toolbar,
  ToolButton,
  ToolGroup,
  useBreakpointMdUp,
  useToast,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  RegistrationState,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { alpha } from "@mui/material/styles";
import { useMediaQuery } from "@mui/system";
import { useCallback, useEffect, useState, type JSX } from "react";
import { useDataPreparationOverlayElements } from "../data-preparation-overlay-context";
import { useIsDataPreparationReadOnly } from "../hooks/use-is-data-preparation-read-only";
import { ReactComponent as DeleteConnectionSvg } from "../icons/registration-edge-delete.svg";
import { ReactComponent as AddConnectionSvg } from "../icons/registration-edge-new.svg";
import { ReactComponent as EditRegistrationSvg } from "../icons/registration-interactive.svg";
import { ReactComponent as RefineRegistrationSvg } from "../icons/registration-refine-automatic.svg";
import { ReactComponent as TransformMoveSvg } from "../icons/transform-move.svg";
import { useSaveUserEdits } from "../inspect-and-publish/refine-and-publish-utils";
import { loadRevisionDataThunk } from "../loading/load-revision-data-thunk";
import { setSelectedEntityIds } from "../store/data-preparation-ui/data-preparation-ui-slice";
import { selectRegistrationRevision } from "../store/revision/revision-entity-selectors";
import {
  selectEditMode,
  selectIsRevisionUserModified,
} from "../store/user-edits/data-preparation-user-edit-selectors";
import {
  disableEditMode,
  enableDefaultEditMode,
  setEditMode,
} from "../store/user-edits/data-preparation-user-edit-slice";
import { RegistrationEditMode } from "../store/user-edits/user-edit-types";
import { Projection } from "../ui/projection-switch";
import { SavingUserEditsDialog } from "./data-preparation-dialogs/saving-user-edits-dialog";
import { StartRefinementDialog } from "./data-preparation-dialogs/start-refinement-dialog";
import { UndoRedoToolbar } from "./undo-redo-toolbar";

const HIGHLIGHT_OPACITY = 0.16;
export const SELECTION_STYLING_EDITING_BUTTONS = {
  color: blue[400],
  backgroundColor: alpha(blue[400], HIGHLIGHT_OPACITY),
};

const EDITING_UNSUPPORTED_TOAST: OpenToastOptions = {
  key: "editing unsupported",
  title: "Editing is not supported on your current device",
  message:
    "This workflow isn’t available on touch devices or small screens. For the best editing experience, please use a device with a larger screen and a mouse.",
  variant: "warning",
};

/** @returns Toolbar where the user can select which edit mode to use. */
export function EditModeToolbar(): JSX.Element | null {
  const isReadOnly = useIsDataPreparationReadOnly();
  return isReadOnly ? null : <EditableModeToolbar />;
}

function EditableModeToolbar(): JSX.Element | null {
  const dispatch = useAppDispatch();
  const editMode = useAppSelector(selectEditMode);
  const { projectApiClient } = useApiClientContext();
  const { projection } = useDataPreparationOverlayElements();
  const revision = useAppSelector(selectRegistrationRevision);
  const disabledMessage = useEditButtonDisabledMessage(revision?.state);
  const { openToast, closeToast } = useToast();
  const isRevisionUserModified = useAppSelector(selectIsRevisionUserModified);
  const [isRefinementDialogOpen, setIsRefinementDialogOpen] =
    useState<boolean>(false);

  const isRefineEnabled =
    revision?.state === RegistrationState.started || isRevisionUserModified;
  const { isSavingUserEdits, saveUserEdits } = useSaveUserEdits();

  /** change the given edit mode to specific registration edit mode  */
  const changeEditMode = useCallback(
    (mode: RegistrationEditMode) => {
      if (editMode === mode) {
        // If clicking on the already enabled edit mode, switch to the default instead
        dispatch(enableDefaultEditMode());
      } else {
        dispatch(setEditMode(mode));
        if (mode === RegistrationEditMode.addConnection) {
          dispatch(setSelectedEntityIds([]));
        }
      }
    },
    [dispatch, editMode],
  );

  const isSmallScreen = !useBreakpointMdUp();
  const isTouch = useMediaQuery("(pointer: coarse)");
  const isEditingUnsupported = isSmallScreen || isTouch;

  /** Toggle the given edit mode on or off. */
  const toggleEditMode = useCallback(async () => {
    if (isEditingUnsupported) {
      openToast(EDITING_UNSUPPORTED_TOAST);
      return;
    }
    if (editMode) {
      // save the user edits and exit edit mode
      if (revision && isRevisionUserModified) {
        await saveUserEdits(revision);
        await dispatch(loadRevisionDataThunk({ projectApiClient }));
      }
      dispatch(disableEditMode());
    } else {
      dispatch(enableDefaultEditMode());
    }
  }, [
    projectApiClient,
    isEditingUnsupported,
    editMode,
    openToast,
    revision,
    isRevisionUserModified,
    dispatch,
    saveUserEdits,
  ]);

  const editRegistrationButton = (
    <FaroTooltip
      title={disabledMessage ?? "Edit Registration"}
      placement="left"
    >
      <ToolButton
        disabled={!!disabledMessage}
        selected={!!editMode}
        onClick={toggleEditMode}
        aria-label="Edit Registration"
      >
        <FaroSvgIcon source={EditRegistrationSvg} />
      </ToolButton>
    </FaroTooltip>
  );

  // Disable editing when unsupported
  useEffect(() => {
    if (isEditingUnsupported) {
      if (editMode) {
        dispatch(disableEditMode());
        openToast(EDITING_UNSUPPORTED_TOAST);
      }
    } else {
      closeToast(EDITING_UNSUPPORTED_TOAST.key);
    }
  }, [dispatch, closeToast, openToast, isEditingUnsupported, editMode]);

  // If the edit mode is enabled and the projection is 3D, enable the default edit mode
  // This is to ensure that the edit mode is enabled when the user is in 3D view
  if (editMode && projection === Projection.threeDimensional) {
    dispatch(enableDefaultEditMode());
    return (
      <>
        <Toolbar>
          {editRegistrationButton}
          <UndoRedoToolbar />
        </Toolbar>
        <SavingUserEditsDialog open={isSavingUserEdits} />
      </>
    );
  }

  return (
    <>
      <Toolbar>
        {editRegistrationButton}

        {editMode && (
          <Toolbar>
            <ToolGroup>
              <FaroTooltip
                title="Select Capture to Reposition"
                placement="left"
              >
                <ToolButton
                  aria-label="Select Capture to Reposition"
                  aria-pressed={editMode === RegistrationEditMode.editScans}
                  onClick={() => changeEditMode(RegistrationEditMode.editScans)}
                  sx={
                    editMode === RegistrationEditMode.editScans
                      ? SELECTION_STYLING_EDITING_BUTTONS
                      : {}
                  }
                >
                  <FaroSvgIcon source={TransformMoveSvg} />
                </ToolButton>
              </FaroTooltip>
              <ConnectionEditingTools
                onConnectionEditingModeChange={changeEditMode}
                editMode={editMode}
              />
            </ToolGroup>

            <ToolGroup>
              <UndoRedoToolbar />
            </ToolGroup>

            <ToolGroup>
              <FaroTooltip title="Refine Registration" placement="left">
                <ToolButton
                  aria-label="Refine Registration"
                  disabled={!isRefineEnabled}
                  onClick={() => setIsRefinementDialogOpen(true)}
                >
                  <ChangeIndicatorBadge invisible={!isRefineEnabled}>
                    <FaroSvgIcon source={RefineRegistrationSvg} />
                  </ChangeIndicatorBadge>
                </ToolButton>
              </FaroTooltip>
            </ToolGroup>
          </Toolbar>
        )}
      </Toolbar>
      <SavingUserEditsDialog open={isSavingUserEdits} />
      {isRefinementDialogOpen && revision && (
        <StartRefinementDialog
          revision={revision}
          open={isRefinementDialogOpen}
          onCancel={() => setIsRefinementDialogOpen(false)}
        />
      )}
    </>
  );
}

type ConnectionEditingToolsProps = {
  onConnectionEditingModeChange(mode: RegistrationEditMode): void;
  editMode?: RegistrationEditMode;
};

/**
 * @returns the buttons to edit connections between captures in the registration
 */
export function ConnectionEditingTools({
  onConnectionEditingModeChange,
  editMode,
}: ConnectionEditingToolsProps): JSX.Element {
  return (
    <ToolGroup>
      <FaroTooltip title="Add connection" placement="left">
        <ToolButton
          onClick={() =>
            onConnectionEditingModeChange(RegistrationEditMode.addConnection)
          }
          onChange={() =>
            Analytics.track<ToggleRegistrationEditModeProperties>(
              EventType.toggleAddConnectionMode,
              {
                enabling: editMode !== RegistrationEditMode.addConnection,
              },
            )
          }
          aria-label="Add connection"
          aria-pressed={editMode === RegistrationEditMode.addConnection}
          sx={
            editMode === RegistrationEditMode.addConnection
              ? SELECTION_STYLING_EDITING_BUTTONS
              : {}
          }
        >
          <FaroSvgIcon source={AddConnectionSvg} />
        </ToolButton>
      </FaroTooltip>

      <FaroTooltip title="Delete connection" placement="left">
        <ToolButton
          onClick={() =>
            onConnectionEditingModeChange(RegistrationEditMode.deleteConnection)
          }
          onChange={() =>
            Analytics.track<ToggleRegistrationEditModeProperties>(
              EventType.toggleDeleteConnectionMode,
              {
                enabling: editMode !== RegistrationEditMode.deleteConnection,
              },
            )
          }
          aria-pressed={editMode === RegistrationEditMode.deleteConnection}
          sx={
            editMode === RegistrationEditMode.deleteConnection
              ? SELECTION_STYLING_EDITING_BUTTONS
              : {}
          }
          aria-label="Delete connection"
        >
          <FaroSvgIcon source={DeleteConnectionSvg} />
        </ToolButton>
      </FaroTooltip>
    </ToolGroup>
  );
}

/**
 * @param revisionState The current state of the revision
 * @returns A message to display when the edit button is disabled, or undefined if the button is enabled.
 */
function useEditButtonDisabledMessage(
  revisionState: RegistrationState | undefined,
): string | undefined {
  const hasWritePermission = useAppSelector(selectHasWritePermission);

  if (!hasWritePermission) {
    return "You do not have permission to edit, please contact the project administrator.";
  }
  if (!revisionState) {
    return;
  }

  // Using a switch to check if all states are covered
  switch (revisionState) {
    case RegistrationState.cloudRegistrationStarted:
      return "Cannot edit while a registration is in progress.";
    case RegistrationState.started:
    case RegistrationState.userModified:
    case RegistrationState.registered:
    case RegistrationState.canceled:
    case RegistrationState.merged:
      // Editing is allowed
      break;
  }
}
